import { useEffect, useState, type ReactNode } from "react";
import { PasswordRuleLabel } from "@/components/auth/PasswordRuleLabel";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { getOrganizationPasswordPolicy, updateOrganizationPasswordPolicy } from "@/lib/api";
import { getOrganizationMfaRequired, setOrganizationMfaRequired } from "@/lib/mfa-api";
import {
	CHARACTER_CLASS_RULES,
	FIXED_PASSWORD_RULES,
	type PasswordCharacterRequirements,
} from "@/lib/password-policy";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

const DESCRIPTION_STYLE = {
	margin: 0,
	fontSize: "var(--text-ui)",
	color: "var(--fg-2)",
	lineHeight: "var(--leading-body)",
} as const;

function Section({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 560 }}>
			<h3
				style={{
					margin: 0,
					fontSize: "var(--text-title)",
					fontWeight: 600,
					color: "var(--fg-1)",
				}}
			>
				{title}
			</h3>
			{children}
		</section>
	);
}

/** 組織が変わったら作り直す (key で指定)。 */
function MfaRequirementForm({ organizationId }: { organizationId: string }) {
	const [required, setRequired] = useState<boolean | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [confirming, setConfirming] = useState<boolean | null>(null);

	useEffect(() => {
		let cancelled = false;
		getOrganizationMfaRequired(organizationId)
			.then((value) => {
				if (!cancelled) setRequired(value);
			})
			.catch((err: Error) => {
				if (!cancelled) setLoadError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	async function apply() {
		if (confirming === null) return;
		setRequired(await setOrganizationMfaRequired(organizationId, confirming));
		setConfirming(null);
	}

	if (loadError)
		return <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{loadError}</p>;
	if (required === null) return null;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<p style={DESCRIPTION_STYLE}>
				必須にすると、組織のすべてのユーザーがサインインのたびに、パスワードに加えてパスキーか認証アプリで本人確認します。各ユーザーは左下のアカウントメニューの「多要素認証」から設定できます。
			</p>
			<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
				<span
					data-testid="mfa-required-status"
					style={{ fontSize: "var(--text-ui)", color: "var(--fg-1)" }}
				>
					{required ? "必須" : "任意 (各ユーザーが設定するかを選ぶ)"}
				</span>
				<ConfirmButton
					label={required ? "必須を解除" : "必須にする"}
					variant={required ? "secondary" : "primary"}
					size="sm"
					loading={false}
					onClick={() => setConfirming(!required)}
					data-testid="mfa-required-toggle"
				/>
			</div>
			<ConfirmDialog
				open={confirming !== null}
				title={confirming ? "多要素認証を必須にする" : "多要素認証の必須を解除"}
				message={
					confirming
						? "まだ設定していないユーザーはサインアウトされ、次のサインインで設定を求められます。先にご自身の多要素認証を設定しておく必要があります。"
						: "設定済みのユーザーは引き続き多要素認証でサインインします。未設定のユーザーはパスワードだけでサインインできるようになります。"
				}
				confirmLabel={confirming ? "必須にする" : "解除"}
				confirmVariant={confirming ? "primary" : "destructive"}
				onConfirm={apply}
				onCancel={() => setConfirming(null)}
			/>
		</div>
	);
}

function RequirementCheckbox({
	label,
	characters,
	checked,
	disabled,
	onChange,
	"data-testid": testId,
}: {
	label: string;
	characters?: string;
	checked: boolean;
	disabled: boolean;
	onChange?: (checked: boolean) => void;
	"data-testid"?: string;
}) {
	const cursor = disabled ? "default" : "pointer";
	return (
		<label
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				fontSize: "var(--text-ui)",
				color: "var(--fg-1)",
				cursor,
				userSelect: "none",
			}}
		>
			<input
				type="checkbox"
				data-testid={testId}
				checked={checked}
				disabled={disabled}
				readOnly={!onChange}
				onChange={onChange && ((e) => onChange(e.target.checked))}
				style={{
					width: 14,
					height: 14,
					margin: 0,
					flexShrink: 0,
					accentColor: "var(--accent-solid)",
					cursor,
				}}
			/>
			<PasswordRuleLabel label={label} characters={characters} />
		</label>
	);
}

/** 組織が変わったら作り直す (key で指定)。保存中の応答が別組織のフォームに入らないようにする。 */
function PasswordRequirementsForm({ organizationId }: { organizationId: string }) {
	const [requirements, setRequirements] = useState<PasswordCharacterRequirements | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		let cancelled = false;
		getOrganizationPasswordPolicy(organizationId)
			.then((value) => {
				if (!cancelled) setRequirements(value);
			})
			.catch((err: Error) => {
				console.error(err);
				if (!cancelled) setLoadError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	if (loadError)
		return (
			<p
				data-testid="password-requirements-load-error"
				style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}
			>
				{loadError}
			</p>
		);
	if (requirements === null) return null;

	function toggle(key: keyof PasswordCharacterRequirements, checked: boolean) {
		setRequirements((current) => current && { ...current, [key]: checked });
		setSaved(false);
		setSaveError(null);
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (requirements === null) return;
		setSaveError(null);
		setSaved(false);
		setSaving(true);
		try {
			setRequirements(await updateOrganizationPasswordPolicy(organizationId, requirements));
			setSaved(true);
		} catch (err) {
			console.error(err);
			setSaveError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setSaving(false);
		}
	}

	return (
		<form
			onSubmit={handleSubmit}
			noValidate
			data-testid="password-requirements-form"
			style={{ display: "flex", flexDirection: "column", gap: 16 }}
		>
			<p style={DESCRIPTION_STYLE}>
				招待を受けたときやパスワードを再設定するときに、新しいパスワードに求める条件です。設定済みのパスワードには影響しません。
			</p>
			<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
				<span style={{ fontSize: "var(--text-caption)", fontWeight: 500, color: "var(--fg-2)" }}>
					常に求める条件
				</span>
				<div
					data-testid="password-requirements-fixed"
					style={{ display: "flex", flexDirection: "column", gap: 6 }}
				>
					{FIXED_PASSWORD_RULES.map((rule) => (
						<RequirementCheckbox key={rule.label} label={rule.label} checked disabled />
					))}
				</div>
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
				<span style={{ fontSize: "var(--text-caption)", fontWeight: 500, color: "var(--fg-2)" }}>
					追加の条件 - チェックした条件をすべて満たす必要があります。
				</span>
				{CHARACTER_CLASS_RULES.map((rule) => (
					<RequirementCheckbox
						key={rule.key}
						label={rule.label}
						characters={rule.characters}
						checked={requirements[rule.key]}
						disabled={saving}
						onChange={(checked) => toggle(rule.key, checked)}
						data-testid={`password-requirement-${rule.key}`}
					/>
				))}
			</div>
			{saveError && (
				<p style={{ margin: 0, color: "var(--danger)", fontSize: "var(--text-ui)" }}>{saveError}</p>
			)}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
				{saved && (
					<span
						data-testid="password-requirements-saved"
						style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}
					>
						保存しました。
					</span>
				)}
				<ConfirmButton
					type="submit"
					label="保存"
					loading={saving}
					data-testid="password-requirements-save"
				/>
			</div>
		</form>
	);
}

export function AuthenticationTab() {
	const organizationId = useCurrentUserStore((s) => s.organizationId);
	const organizationName = useCurrentUserStore((s) => s.organizationName);

	return (
		<div style={{ padding: 32, height: "100%", overflowY: "auto" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					height: 40,
					flexShrink: 0,
					marginBottom: 8,
				}}
			>
				<h2
					style={{
						fontSize: "var(--text-heading)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
					}}
				>
					認証
				</h2>
			</div>
			<p style={{ margin: "0 0 24px", fontSize: "var(--text-ui)", color: "var(--fg-2)" }}>
				対象の組織:{" "}
				<span style={{ color: "var(--fg-1)" }}>{organizationName ?? organizationId}</span>
			</p>
			<div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
				<Section title="多要素認証">
					<MfaRequirementForm key={organizationId} organizationId={organizationId} />
				</Section>
				<Section title="パスワード要件">
					<PasswordRequirementsForm key={organizationId} organizationId={organizationId} />
				</Section>
			</div>
		</div>
	);
}
